import { useMutation, type ApolloCache } from "@apollo/client";

import {
  CREATE_OEM_EMAIL_TICKET_RULE_MUTATION,
  DELETE_OEM_EMAIL_TICKET_RULE_MUTATION,
  LIST_OWN_OEM_EMAIL_TICKET_RULES_QUERY,
  UPDATE_OEM_EMAIL_TICKET_RULE_MUTATION,
} from "#/src/api";
import { errorsHandler } from "#/src/transformers";
import type {
  EmailTicketRuleInput,
  EmailTicketRuleType,
} from "~/types/emailTicketRule";

/** Stable codes the backend returns at `extensions.additionalArgs.code`. */
export const EMAIL_TICKET_RULE_ERROR_CODES = {
  ALREADY_EXISTS: "EMAIL_TICKET_RULE_ALREADY_EXISTS",
  EMAIL_ACCOUNT_NOT_FOUND: "EMAIL_ACCOUNT_NOT_FOUND",
  EMAIL_ACCOUNT_NOT_CONNECTED: "EMAIL_ACCOUNT_NOT_CONNECTED",
  SUPPORT_TICKET_TYPE_NOT_FOUND: "SUPPORT_TICKET_TYPE_NOT_FOUND",
  SUPPORT_TICKET_TYPE_IS_SYSTEM: "SUPPORT_TICKET_TYPE_IS_SYSTEM",
  RULE_NOT_FOUND: "EMAIL_TICKET_RULE_NOT_FOUND",
} as const;

export type EmailTicketRuleErrorField = keyof EmailTicketRuleInput;

export type EmailTicketRuleError = {
  message?: string;
  code?: string;
  /** The form field the error belongs to, when it belongs to one. */
  field?: EmailTicketRuleErrorField;
};

// errorsHandler only keeps the message; the guard code and field live at
// `extensions.additionalArgs` (see parseEntityStateError in ~/utils/graphql),
// so carry them across explicitly. Match on the code, never on the message.
const toRuleError = (err): EmailTicketRuleError => {
  const args = err?.graphQLErrors?.[0]?.extensions?.additionalArgs;
  return {
    ...errorsHandler(err),
    code: typeof args?.code === "string" ? args.code : undefined,
    field: typeof args?.field === "string" ? args.field : undefined,
  };
};

const updateRulesCache = (
  cache: ApolloCache<unknown>,
  update: (rules: EmailTicketRuleType[]) => EmailTicketRuleType[],
) => {
  const existing = cache.readQuery<{
    listOwnOemEmailTicketRules: EmailTicketRuleType[];
  }>({ query: LIST_OWN_OEM_EMAIL_TICKET_RULES_QUERY });

  if (!existing) return;

  cache.writeQuery({
    query: LIST_OWN_OEM_EMAIL_TICKET_RULES_QUERY,
    data: {
      listOwnOemEmailTicketRules: update(existing.listOwnOemEmailTicketRules),
    },
  });
};

const removeRuleFromCache = (cache: ApolloCache<unknown>, id: string) =>
  updateRulesCache(cache, (rules) => rules.filter((rule) => rule._id !== id));

export const useCreateEmailTicketRule = () => {
  const [create, { loading }] = useMutation(
    CREATE_OEM_EMAIL_TICKET_RULE_MUTATION,
  );

  return {
    loading,
    createRule: async (input: EmailTicketRuleInput) => {
      try {
        const { data } = await create({
          variables: { input },
          update(cache, { data: { createOemEmailTicketRule: rule } = {} }) {
            if (!rule) return;
            updateRulesCache(cache, (rules) => [...rules, rule]);
          },
        });
        return data?.createOemEmailTicketRule as EmailTicketRuleType;
      } catch (err) {
        throw toRuleError(err);
      }
    },
  };
};

export const useUpdateEmailTicketRule = () => {
  const [update, { loading, client }] = useMutation(
    UPDATE_OEM_EMAIL_TICKET_RULE_MUTATION,
  );

  return {
    loading,
    // The list entry is normalised by _id, so Apollo merges the returned rule
    // into the cached list without a manual cache write.
    updateRule: async (id: string, input: EmailTicketRuleInput) => {
      try {
        const { data } = await update({ variables: { id, input } });
        return data?.updateOemEmailTicketRule as EmailTicketRuleType;
      } catch (err) {
        const ruleError = toRuleError(err);
        // Deleted elsewhere (another admin, or its ticket type was deleted
        // without a transfer): drop the stale card.
        if (ruleError.code === EMAIL_TICKET_RULE_ERROR_CODES.RULE_NOT_FOUND) {
          removeRuleFromCache(client.cache, id);
        }
        throw ruleError;
      }
    },
  };
};

export const useDeleteEmailTicketRule = () => {
  const [remove, { loading, client }] = useMutation(
    DELETE_OEM_EMAIL_TICKET_RULE_MUTATION,
  );

  return {
    loading,
    deleteRule: async (id: string) => {
      try {
        await remove({
          variables: { id },
          update(cache) {
            removeRuleFromCache(cache, id);
          },
        });
      } catch (err) {
        const ruleError = toRuleError(err);
        // Already gone is the outcome the admin asked for.
        if (ruleError.code === EMAIL_TICKET_RULE_ERROR_CODES.RULE_NOT_FOUND) {
          removeRuleFromCache(client.cache, id);
          return;
        }
        throw ruleError;
      }
    },
  };
};
